import { ApolloLink, Observable } from "@apollo/client";
import { DeferLabelTracker, summarizePending } from "./DeferLabelTracker";

export { DeferLabelTracker };

type MultipartChunk = {
  hasNext: boolean;
  incremental?: unknown[];
  pending?: unknown[];
  completed?: unknown[];
  data?: Record<string, unknown> | null;
};

type DebugContext = {
  debugTiming?: boolean;
};

function isMultipartChunk(
  result: ApolloLink.Result
): result is ApolloLink.Result & MultipartChunk {
  return "hasNext" in result;
}

class DebugIncrementalLink extends ApolloLink {
  constructor({ enabled = true }: { enabled?: boolean } = {}) {
    super((operation, forward) => {
      const { debugTiming } = operation.getContext() as DebugContext;
      if (!enabled || debugTiming !== true) {
        return forward(operation);
      }

      const name = operation.operationName ?? "anonymous";
      const start = performance.now();
      const at = () => `+${Math.round(performance.now() - start)}ms`;
      const tracker = new DeferLabelTracker();
      let sawInitial = false;

      const logLabelSettled = (label: string) => {
        const patches = tracker.patchCountForLabel(label);
        const patchInfo =
          patches > 0
            ? ` (${String(patches)} patch${patches === 1 ? "" : "es"})`
            : "";
        console.info(
          `[multipart:${name}] defer "${label}" settled${patchInfo} at ${at()}`
        );
      };

      console.info(
        `[multipart:${name}] request sent at ${new Date().toISOString()}`
      );

      return new Observable<ApolloLink.Result>((subscriber) => {
        const subscription = forward(operation).subscribe({
          next: (result) => {
            const chunk = isMultipartChunk(result) ? result : null;

            if (chunk === null) {
              console.info(`[multipart:${name}] single response at ${at()}`);
            } else {
              if (!sawInitial) {
                sawInitial = true;
                tracker.observeInitialData(chunk.data);
                tracker.observePending(chunk.pending);

                const pendingSummary = summarizePending(chunk.pending);
                console.info(
                  `[multipart:${name}] initial payload at ${at()}${
                    pendingSummary !== undefined
                      ? ` (pending: ${pendingSummary})`
                      : ""
                  }`
                );
              } else {
                tracker.observePending(chunk.pending);
              }

              const settledFromIncremental = tracker.observeIncremental(
                chunk.incremental
              );
              const settledFromCompleted = tracker.observeCompleted(
                chunk.completed
              );

              for (const label of settledFromIncremental) {
                logLabelSettled(label);
              }
              for (const label of settledFromCompleted) {
                logLabelSettled(label);
              }
            }

            subscriber.next(result);
          },
          error: (error: unknown) => {
            console.info(`[multipart:${name}] error at ${at()}`);
            subscriber.error(error);
          },
          complete: () => {
            console.info(`[multipart:${name}] stream complete at ${at()}`);
            subscriber.complete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      });
    });
  }
}

export { DebugIncrementalLink };
